import { getCurrentInstance, onBeforeUnmount } from "vue";

/**
 * Returns an AbortController that is automatically aborted when the current
 * component unmounts. Safe to call outside a component setup (the unmount
 * hook is registered only when an instance is available).
 */
export function useComponentAbortController(): AbortController {
  const controller = new AbortController();
  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      if (!controller.signal.aborted) {
        controller.abort();
      }
    });
  }
  return controller;
}

/**
 * Strict abort check: returns true *only* when we have positive evidence the
 * request was aborted — the signal has flipped to `aborted`, or the error's
 * name is `AbortError`. Does not apply the "Failed to fetch" teardown
 * heuristic, so genuine network outages (offline/DNS/CORS) still propagate.
 *
 * Prefer this for user-initiated mutations and user-visible reads where
 * silently swallowing a real failure would leave the UI inconsistent or the
 * user without feedback.
 */
export function isExplicitAbortError(error: unknown, signal?: AbortSignal): boolean {
  if (signal?.aborted) {
    return true;
  }

  if (!(error instanceof Error)) {
    return false;
  }

  if (error.name === "AbortError") {
    return true;
  }

  const apolloLike = error as { networkError?: { name?: string } | null };
  return apolloLike.networkError?.name === "AbortError";
}

/**
 * Detects errors that originate from a deliberately aborted request or a
 * browser tearing down an in-flight fetch (e.g. Playwright closing the
 * page context, user switching tabs mid-flight). Matching errors should
 * be treated as benign — the caller no longer cares about the result.
 *
 * The `TypeError: Failed to fetch` heuristic is gated by two signals:
 *   1. The caller supplied an `AbortSignal` (the request was tied to a
 *      lifecycle), AND
 *   2. `document.hidden === true` (the tab is backgrounded or the
 *      browser context is closing).
 *
 * Requiring `document.hidden` means a live, visible page hitting a real
 * network outage still surfaces the error — only failures on a
 * non-interactive tab are classified as teardown. Safe to use on
 * user-initiated mutations and writes; for an even stricter check that
 * only trusts explicit abort signals, use [[isExplicitAbortError]].
 */
export function isAbortOrTeardownError(error: unknown, signal?: AbortSignal): boolean {
  if (isExplicitAbortError(error, signal)) {
    return true;
  }

  if (!(error instanceof Error)) {
    return false;
  }

  const apolloLike = error as { networkError?: { name?: string; message?: string } | null };
  const networkError = apolloLike.networkError;
  if (!networkError) {
    return false;
  }

  // A TypeError from the fetch layer is emitted for both deliberate aborts
  // and genuine network failures. Trust it as teardown only when we have
  // two corroborating signals: the caller tied this request to a signal,
  // AND the document is currently hidden. A visible tab hitting "Failed to
  // fetch" is a real outage that callers should surface.
  // The message differs by runtime: browsers say "Failed to fetch", Node's
  // undici says "fetch failed".
  if (
    signal &&
    isDocumentHidden() &&
    networkError.name === "TypeError" &&
    isFetchFailureMessage(networkError.message)
  ) {
    return true;
  }

  return false;
}

function isDocumentHidden(): boolean {
  return typeof document !== "undefined" && document.hidden === true;
}

function isFetchFailureMessage(message: string | undefined): boolean {
  return message === "Failed to fetch" || message === "fetch failed";
}

/**
 * Looser teardown check for store-level fetches that have no lifecycle signal
 * to tie to. Returns true when the underlying fetch failed (`TypeError: Failed
 * to fetch` / `fetch failed`) *and* the document is hidden — the two signals
 * that reliably indicate the browser is tearing down in-flight requests
 * (Playwright closing a page, tab backgrounded during SPA navigation).
 *
 * Prefer [[isAbortOrTeardownError]] when you can pass an `AbortSignal`; this
 * variant exists for background/store fetches where wiring a signal through
 * every call site would be disproportionate.
 */
export function isPageHiddenTeardown(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  if (!isDocumentHidden()) {
    return false;
  }
  const apolloLike = error as { networkError?: { name?: string; message?: string } | null };
  const networkError = apolloLike.networkError;
  if (!networkError) {
    return false;
  }
  return networkError.name === "TypeError" && isFetchFailureMessage(networkError.message);
}

/**
 * Detects fetch-layer failures (`TypeError: Failed to fetch` /
 * `fetch failed`) regardless of `document.hidden`. Apollo surfaces these
 * when the browser tears down an in-flight request — common during SPA
 * navigation and Playwright test transitions, where the document is still
 * "visible" by the time the error fires.
 *
 * Use ONLY for fire-and-forget background refreshes where a missed error is
 * not user-impacting (the next render or SSE event will re-fetch). Genuine
 * outages on critical paths still need the stricter [[isAbortOrTeardownError]]
 * (which requires an AbortSignal + `document.hidden`) so they aren't
 * silenced.
 */
export function isFetchLayerNetworkFailure(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  const apolloLike = error as { networkError?: { name?: string; message?: string } | null };
  const networkError = apolloLike.networkError;
  if (!networkError) {
    return false;
  }
  return networkError.name === "TypeError" && isFetchFailureMessage(networkError.message);
}

/**
 * Build the Apollo query `context` option needed to forward an AbortSignal
 * into the underlying `fetch` call. Returns `undefined` when no signal is
 * supplied so callers can spread it without leaking an empty context.
 */
export function apolloAbortContext(signal?: AbortSignal): { fetchOptions: { signal: AbortSignal } } | undefined {
  if (!signal) {
    return undefined;
  }
  return { fetchOptions: { signal } };
}
